import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import PoweredBy from '../PoweredBy';

type ScanState = 'ready' | 'scanning' | 'detected' | 'assessing';

interface DetectedItem {
  id: string;
  name: string;
  category: string;
  size: string;
  color: string;
  condition: 'excellent' | 'good' | 'fair';
  originalPrice: number;
  tradeInValue: number;
  image: string;
}

// Simulated Lululemon product catalog
const LULULEMON_CATALOG = [
  {
    id: 'align-pant-25',
    name: 'Align High-Rise Pant 25"',
    category: 'Leggings',
    originalPrice: 98,
    image: 'https://images.lululemon.com/is/image/lululemon/LW5CTIS_026083_1?wid=300'
  },
  {
    id: 'scuba-half-zip',
    name: 'Scuba Oversized Half-Zip Hoodie',
    category: 'Hoodies',
    originalPrice: 118,
    image: 'https://images.lululemon.com/is/image/lululemon/LW3GERS_0001_1?wid=300'
  },
  {
    id: 'wunder-train-28',
    name: 'Wunder Train High-Rise Tight 28"',
    category: 'Leggings',
    originalPrice: 98,
    image: 'https://images.lululemon.com/is/image/lululemon/LW5DQBS_031382_1?wid=300'
  },
  {
    id: 'define-jacket',
    name: 'Define Jacket Luon',
    category: 'Jackets',
    originalPrice: 118,
    image: 'https://images.lululemon.com/is/image/lululemon/LW4AWLS_0001_1?wid=300'
  },
  {
    id: 'swiftly-tech',
    name: 'Swiftly Tech Long Sleeve Shirt 2.0',
    category: 'Tops',
    originalPrice: 78,
    image: 'https://images.lululemon.com/is/image/lululemon/LW3DNAS_031382_1?wid=300'
  }
];

const LululemonScanView: React.FC = () => {
  const navigate = useNavigate();
  const { dispatch } = useAppContext();
  const [scanState, setScanState] = useState<ScanState>('ready');
  const [detectedItems, setDetectedItems] = useState<DetectedItem[]>([]);
  const [currentItem, setCurrentItem] = useState<DetectedItem | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    // Start camera if available
    startCamera();
    return () => stopCamera();
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment', width: 1280, height: 720 } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.log('Camera not available, using demo mode');
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = (videoRef.current.srcObject as MediaStream).getTracks();
      tracks.forEach(track => track.stop());
    }
  };

  const simulateItemDetection = () => {
    setScanState('scanning');
    
    // Simulate detection delay
    setTimeout(() => {
      // Randomly select a product from catalog
      const randomProduct = LULULEMON_CATALOG[Math.floor(Math.random() * LULULEMON_CATALOG.length)];
      const conditions: ('excellent' | 'good' | 'fair')[] = ['excellent', 'good', 'fair'];
      const randomCondition = conditions[Math.floor(Math.random() * conditions.length)];
      const sizes = ['4', '6', '8', '10'];
      const colors = ['Black', 'True Navy', 'Dark Olive', 'Heathered Core Ultra Light Grey'];
      
      // Calculate trade-in value based on condition
      const conditionMultipliers = { excellent: 0.5, good: 0.35, fair: 0.2 };
      const tradeInValue = Math.round(randomProduct.originalPrice * conditionMultipliers[randomCondition]);

      const item: DetectedItem = {
        id: `${randomProduct.id}-${Date.now()}`,
        name: randomProduct.name,
        category: randomProduct.category,
        size: sizes[Math.floor(Math.random() * sizes.length)],
        color: colors[Math.floor(Math.random() * colors.length)],
        condition: randomCondition,
        originalPrice: randomProduct.originalPrice,
        tradeInValue: tradeInValue,
        image: randomProduct.image
      };

      setCurrentItem(item);
      setScanState('detected');

      // Show assessment animation
      setTimeout(() => {
        setScanState('assessing');
        setTimeout(() => {
          setDetectedItems(prev => [...prev, item]);
          setScanState('ready');
          setCurrentItem(null);
        }, 1500);
      }, 1000);
    }, 2000);
  };

  const handleAddMore = () => {
    simulateItemDetection();
  };

  const handleContinue = () => {
    // Store items in context
    detectedItems.forEach(item => {
      dispatch({
        type: 'ADD_EMPTY',
        payload: {
          id: item.id,
          name: item.name,
          size: item.size,
          image: item.image,
          weight: 0.2,
          isBranded: true,
          rewards: {
            loyaltyPoints: Math.round(item.tradeInValue * 10),
            dollarValue: `$${item.tradeInValue}`,
            discountValue: '10'
          }
        }
      });
    });
    navigate('/lululemon/offer');
  };

  const handleBack = () => {
    navigate('/lululemon/before-start');
  };

  const handleRemoveItem = (itemId: string) => {
    setDetectedItems(prev => prev.filter(i => i.id !== itemId));
  };

  const totalTradeInValue = detectedItems.reduce((sum, item) => sum + item.tradeInValue, 0);

  const getConditionColor = (condition: string) => {
    switch (condition) {
      case 'excellent': return '#22C55E';
      case 'good': return '#F59E0B';
      case 'fair': return '#EF4444';
      default: return '#FFFFFF';
    }
  };

  return (
    <motion.div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{ backgroundColor: '#1A1A1A' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col">
        {/* Header */}
        <div className="flex justify-center mx-20 mt-10">
          <div className="w-full max-w-[1200px] flex justify-between items-center">
            <img 
              src="/assets/images/logos/lululemon-white.png" 
              alt="lululemon" 
              className="h-8"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
            <div className="flex items-center gap-4">
              <span className="text-white/60 text-sm">Items: {detectedItems.length}</span>
              <span className="text-white font-medium">${totalTradeInValue} Credit</span>
            </div>
          </div>
        </div>

        {/* Main Content - Two Column Layout */}
        <div className="flex-1 flex gap-12 px-20 py-8">
          {/* Left: Camera/Scan Area */}
          <div className="flex-1 flex flex-col">
            {/* Camera View */}
            <div 
              className="relative flex-1 rounded-2xl overflow-hidden"
              style={{ backgroundColor: '#000', border: '2px solid rgba(255,255,255,0.1)' }}
            >
              {/* Video Feed */}
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="absolute inset-0 w-full h-full object-cover"
              />

              {/* Scan Overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                {scanState === 'ready' && (
                  <motion.div
                    className="text-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <div 
                      className="w-48 h-48 mx-auto mb-6 rounded-lg"
                      style={{ 
                        border: '2px dashed rgba(255,255,255,0.4)',
                        backgroundColor: 'rgba(255,255,255,0.05)'
                      }}
                    />
                    <p className="text-white/80 text-lg mb-4">Place item on the platform</p>
                    <button
                      onClick={simulateItemDetection}
                      className="px-8 py-3 rounded-lg text-white font-medium uppercase tracking-wider"
                      style={{ backgroundColor: '#D31334' }}
                    >
                      Scan Item
                    </button>
                  </motion.div>
                )}

                {scanState === 'scanning' && (
                  <motion.div
                    className="text-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <motion.div
                      className="w-48 h-48 mx-auto mb-6 rounded-lg flex items-center justify-center"
                      style={{ border: '2px solid #D31334' }}
                      animate={{ 
                        boxShadow: ['0 0 20px rgba(211,19,52,0.3)', '0 0 40px rgba(211,19,52,0.6)', '0 0 20px rgba(211,19,52,0.3)']
                      }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      <motion.div
                        className="w-8 h-8 rounded-full"
                        style={{ border: '3px solid #D31334', borderTopColor: 'transparent' }}
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      />
                    </motion.div>
                    <p className="text-white/80 text-lg">Scanning...</p>
                  </motion.div>
                )}

                {(scanState === 'detected' || scanState === 'assessing') && currentItem && (
                  <motion.div
                    className="text-center w-full max-w-md mx-auto"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    <div className="bg-black/80 backdrop-blur-md rounded-2xl p-6">
                      <img 
                        src={currentItem.image} 
                        alt={currentItem.name}
                        className="w-32 h-32 mx-auto mb-4 object-contain rounded-lg bg-white"
                      />
                      <h3 className="text-white text-xl font-medium mb-2">{currentItem.name}</h3>
                      <p className="text-white/60 mb-4">Size {currentItem.size} · {currentItem.color}</p>
                      
                      {scanState === 'assessing' && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                        >
                          <div className="flex items-center justify-center gap-2 mb-2">
                            <span className="text-white/60">Condition:</span>
                            <span 
                              className="uppercase font-medium"
                              style={{ color: getConditionColor(currentItem.condition) }}
                            >
                              {currentItem.condition}
                            </span>
                          </div>
                          <div className="text-3xl font-bold" style={{ color: '#D31334' }}>
                            ${currentItem.tradeInValue} Credit
                          </div>
                        </motion.div>
                      )}
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </div>

          {/* Right: Items List */}
          <div className="w-96 flex flex-col">
            <h2 className="text-white text-2xl font-light mb-6">Your Items</h2>
            
            <div className="flex-1 overflow-y-auto space-y-4 mb-6">
              <AnimatePresence>
                {detectedItems.length === 0 ? (
                  <motion.div
                    className="text-center py-12"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <p className="text-white/40">No items scanned yet</p>
                    <p className="text-white/30 text-sm mt-2">Scan your first item to get started</p>
                  </motion.div>
                ) : (
                  detectedItems.map((item, index) => (
                    <motion.div
                      key={item.id}
                      className="flex gap-4 p-4 rounded-xl"
                      style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <img 
                        src={item.image}
                        alt={item.name}
                        className="w-20 h-20 object-contain rounded-lg bg-white"
                      />
                      <div className="flex-1">
                        <h4 className="text-white text-sm font-medium mb-1">{item.name}</h4>
                        <p className="text-white/50 text-xs mb-2">Size {item.size} · {item.color}</p>
                        <div className="flex items-center gap-2">
                          <span 
                            className="text-xs uppercase px-2 py-0.5 rounded"
                            style={{ 
                              backgroundColor: `${getConditionColor(item.condition)}20`,
                              color: getConditionColor(item.condition)
                            }}
                          >
                            {item.condition}
                          </span>
                          <span className="text-white font-medium">${item.tradeInValue}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveItem(item.id)}
                        className="text-white/40 hover:text-white/80 transition-colors self-start"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </motion.div>
                  ))
                )}
              </AnimatePresence>
            </div>

            {/* Total */}
            {detectedItems.length > 0 && (
              <div 
                className="p-4 rounded-xl mb-6"
                style={{ backgroundColor: 'rgba(211,19,52,0.1)', border: '1px solid rgba(211,19,52,0.3)' }}
              >
                <div className="flex justify-between items-center">
                  <span className="text-white/80">Total Trade-In Value</span>
                  <span className="text-3xl font-bold" style={{ color: '#D31334' }}>
                    ${totalTradeInValue}
                  </span>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-3">
              {detectedItems.length > 0 && (
                <>
                  <button
                    onClick={handleAddMore}
                    disabled={scanState !== 'ready'}
                    className="w-full py-3 rounded-lg text-white uppercase tracking-wider transition-opacity disabled:opacity-50"
                    style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                  >
                    Add Another Item
                  </button>
                  <button
                    onClick={handleContinue}
                    className="w-full py-3 rounded-lg text-white uppercase tracking-wider font-medium"
                    style={{ backgroundColor: '#D31334' }}
                  >
                    Continue
                  </button>
                </>
              )}
              <button
                onClick={handleBack}
                className="w-full text-white/50 hover:text-white text-sm uppercase tracking-wider py-2 transition-colors"
              >
                Back
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-center pb-8">
          <PoweredBy />
        </div>
      </div>
    </motion.div>
  );
};

export default LululemonScanView;

